import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'

const MIN_SCALE = 1
const MAX_SCALE = 4
const DOUBLE_TAP_SCALE = 2.5
const DOUBLE_TAP_MS = 300
const SWIPE_THRESHOLD_PX = 48
const DISMISS_THRESHOLD_PX = 80
const ZOOM_STEP = 0.5

type ZoomPanState = {
  scale: number
  translateX: number
  translateY: number
}

type UseImageZoomPanOptions = {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeDown?: () => void
}

type ImageZoomPanHandlers = {
  scale: number
  transformStyle: { transform: string }
  onWheel: (event: ReactWheelEvent) => void
  onPointerDown: (event: ReactPointerEvent) => void
  onPointerMove: (event: ReactPointerEvent) => void
  onPointerUp: (event: ReactPointerEvent) => void
  onPointerCancel: (event: ReactPointerEvent) => void
  onDoubleClick: () => void
  zoomIn: () => void
  zoomOut: () => void
  reset: () => void
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function boundPan(scale: number, tx: number, ty: number, width: number, height: number) {
  if (scale <= MIN_SCALE) {
    return { translateX: 0, translateY: 0 }
  }
  const maxX = (width * (scale - 1)) / 2
  const maxY = (height * (scale - 1)) / 2
  return {
    translateX: clamp(tx, -maxX, maxX),
    translateY: clamp(ty, -maxY, maxY),
  }
}

export function useImageZoomPan({
  onSwipeLeft,
  onSwipeRight,
  onSwipeDown,
}: UseImageZoomPanOptions = {}): ImageZoomPanHandlers {
  const containerRef = useRef<HTMLElement | null>(null)
  const [state, setState] = useState<ZoomPanState>({ scale: MIN_SCALE, translateX: 0, translateY: 0 })
  const stateRef = useRef(state)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null)
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null)
  const swipeStart = useRef<{ x: number; y: number } | null>(null)
  const pinched = useRef(false)
  const lastTap = useRef(0)

  const box = () => containerRef.current?.getBoundingClientRect()

  const reset = useCallback(() => {
    setState({ scale: MIN_SCALE, translateX: 0, translateY: 0 })
    pointers.current.clear()
    panStart.current = null
    pinchStart.current = null
    swipeStart.current = null
    pinched.current = false
  }, [])

  const clampScale = (value: number) => clamp(value, MIN_SCALE, MAX_SCALE)

  const applyScale = useCallback((nextScale: number) => {
    const rect = box()
    setState((prev) => {
      const scale = clampScale(nextScale)
      if (scale === MIN_SCALE) {
        return { scale: MIN_SCALE, translateX: 0, translateY: 0 }
      }
      const w = rect?.width ?? 1
      const h = rect?.height ?? 1
      return { scale, ...boundPan(scale, prev.translateX, prev.translateY, w, h) }
    })
  }, [])

  const zoomIn = useCallback(() => {
    applyScale(stateRef.current.scale + ZOOM_STEP)
  }, [applyScale])

  const zoomOut = useCallback(() => {
    applyScale(stateRef.current.scale - ZOOM_STEP)
  }, [applyScale])

  const getRelativePoint = useCallback((clientX: number, clientY: number) => {
    const rect = box()
    if (!rect) {
      return { x: clientX, y: clientY }
    }
    return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 }
  }, [])

  const onWheel = useCallback(
    (event: ReactWheelEvent) => {
      event.preventDefault()
      const delta = event.deltaY > 0 ? -0.15 : 0.15
      const rect = box()
      setState((prev) => {
        const nextScale = clampScale(prev.scale + delta * prev.scale)
        if (nextScale === MIN_SCALE) {
          return { scale: MIN_SCALE, translateX: 0, translateY: 0 }
        }
        const point = getRelativePoint(event.clientX, event.clientY)
        const ratio = nextScale / prev.scale
        const tx = prev.translateX - point.x * (ratio - 1)
        const ty = prev.translateY - point.y * (ratio - 1)
        const w = rect?.width ?? 1
        const h = rect?.height ?? 1
        return { scale: nextScale, ...boundPan(nextScale, tx, ty, w, h) }
      })
    },
    [getRelativePoint],
  )

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    containerRef.current = event.currentTarget as HTMLElement
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()]
      const distance = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
      pinchStart.current = { distance, scale: stateRef.current.scale }
      panStart.current = null
      swipeStart.current = null
      pinched.current = true
      return
    }

    if (stateRef.current.scale > MIN_SCALE) {
      panStart.current = {
        x: event.clientX,
        y: event.clientY,
        tx: stateRef.current.translateX,
        ty: stateRef.current.translateY,
      }
      swipeStart.current = null
      return
    }

    swipeStart.current = { x: event.clientX, y: event.clientY }
    panStart.current = null
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent) => {
    if (!pointers.current.has(event.pointerId)) {
      return
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const rect = box()
    const w = rect?.width ?? 1
    const h = rect?.height ?? 1

    if (pointers.current.size === 2 && pinchStart.current) {
      const pts = [...pointers.current.values()]
      const distance = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
      const nextScale = clampScale(pinchStart.current.scale * (distance / Math.max(pinchStart.current.distance, 1)))
      setState((prev) => ({
        scale: nextScale,
        ...boundPan(nextScale, prev.translateX, prev.translateY, w, h),
      }))
      return
    }

    if (pointers.current.size === 1 && panStart.current && stateRef.current.scale > MIN_SCALE) {
      const dx = event.clientX - panStart.current.x
      const dy = event.clientY - panStart.current.y
      const next = boundPan(
        stateRef.current.scale,
        panStart.current.tx + dx,
        panStart.current.ty + dy,
        w,
        h,
      )
      setState((prev) => ({ ...prev, ...next }))
    }
  }, [])

  const releasePointer = useCallback((event: ReactPointerEvent) => {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) {
      pinchStart.current = null
    }
    if (pointers.current.size === 0) {
      panStart.current = null
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  const onPointerUp = useCallback(
    (event: ReactPointerEvent) => {
      const start = swipeStart.current
      const scale = stateRef.current.scale
      const wasPinch = pinched.current
      releasePointer(event)

      if (pointers.current.size === 0) {
        pinched.current = false
        swipeStart.current = null
      }

      if (wasPinch || scale > MIN_SCALE || !start || event.button !== 0) {
        return
      }

      const dx = event.clientX - start.x
      const dy = event.clientY - start.y
      if (dy > DISMISS_THRESHOLD_PX && dy > Math.abs(dx)) {
        onSwipeDown?.()
        return
      }
      if (Math.abs(dx) >= SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          onSwipeLeft?.()
        } else {
          onSwipeRight?.()
        }
      }
    },
    [onSwipeDown, onSwipeLeft, onSwipeRight, releasePointer],
  )

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent) => {
      pinched.current = false
      swipeStart.current = null
      releasePointer(event)
    },
    [releasePointer],
  )

  const onDoubleClick = useCallback(() => {
    setState((prev) => {
      if (prev.scale > MIN_SCALE) {
        return { scale: MIN_SCALE, translateX: 0, translateY: 0 }
      }
      return { scale: DOUBLE_TAP_SCALE, translateX: 0, translateY: 0 }
    })
  }, [])

  const handlePointerDownWithTap = useCallback(
    (event: ReactPointerEvent) => {
      const now = Date.now()
      if (now - lastTap.current <= DOUBLE_TAP_MS) {
        lastTap.current = 0
        onDoubleClick()
        return
      }
      lastTap.current = now
      onPointerDown(event)
    },
    [onDoubleClick, onPointerDown],
  )

  const transformStyle = {
    transform: `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`,
  }

  return {
    scale: state.scale,
    transformStyle,
    onWheel,
    onPointerDown: handlePointerDownWithTap,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onDoubleClick,
    zoomIn,
    zoomOut,
    reset,
  }
}
